import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Settings as SettingsIcon } from 'lucide-react';
import { loadUserState } from '../lib/user-state';
import { getCategory } from '../lib/categories';
/**
 * TopBar M2 cyanotype — 2 modes :
 *
 * - root : avatar carre + greeting Bonjour + Settings (sur `/` et `/library`)
 * - back : chevron back + titre page + Settings (sur sub-pages
 *   /library/:cat, /library/:cat/:game, /profile, /settings, /privacy)
 *
 * Pattern iOS native : back-button monte d'un niveau dans la hierarchie de
 * navigation, jamais navigate(-1) (un user peut atterrir directement sur une
 * sub-page via deep link).
 */
function getInitials(displayName) {
    if (!displayName || displayName.trim().length === 0)
        return 'VK';
    const parts = displayName.trim().split(/\s+/);
    if (parts.length === 1)
        return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function getFirstName(displayName) {
    if (!displayName || displayName.trim().length === 0)
        return null;
    return displayName.trim().split(/\s+/)[0];
}
function humanizeSlug(slug) {
    return slug
        .split('-')
        .map((p) => (p.length > 0 ? p[0].toUpperCase() + p.slice(1) : p))
        .join(' ');
}
function ChevronLeft({ size = 20 }) {
    return (_jsx("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true, children: _jsx("polyline", { points: "15 18 9 12 15 6" }) }));
}
/**
 * Resout (parent path, page title) selon la pathname courante.
 * Renvoie null si la TopBar reste en mode root.
 */
function resolveBackContext(pathname, t) {
    if (pathname === '/' || pathname === '/library')
        return null;
    const gameMatch = /^\/library\/([^/]+)\/([^/]+)\/?$/.exec(pathname);
    if (gameMatch) {
        return {
            to: `/library/${gameMatch[1]}`,
            title: humanizeSlug(gameMatch[2]),
        };
    }
    const catMatch = /^\/library\/([^/]+)\/?$/.exec(pathname);
    if (catMatch) {
        const cat = getCategory(catMatch[1]);
        const label = cat
            ? t(`library:categories.${cat.slug}.label`, {
                defaultValue: humanizeSlug(catMatch[1]),
            })
            : humanizeSlug(catMatch[1]);
        return { to: '/library', title: label };
    }
    if (pathname === '/profile' || pathname.startsWith('/profile/')) {
        return { to: '/', title: t('nav.page_profile', { defaultValue: 'Profil' }) };
    }
    if (pathname === '/settings' || pathname.startsWith('/settings/')) {
        return {
            to: '/',
            title: t('nav.page_settings', { defaultValue: 'Réglages' }),
        };
    }
    if (pathname === '/privacy' || pathname.startsWith('/privacy/')) {
        return {
            to: '/settings',
            title: t('nav.page_privacy', { defaultValue: 'Confidentialité' }),
        };
    }
    return null;
}
export function TopBar() {
    const { t } = useTranslation(['common', 'library']);
    const location = useLocation();
    const navigate = useNavigate();
    const back = resolveBackContext(location.pathname, t);
    if (back) {
        return (_jsx("header", { className: "\n          sticky top-0 z-40\n          bg-cyan-surface\n          border-b border-cyan-border-faint\n          pt-[env(safe-area-inset-top)]\n        ", children: _jsxs("div", { className: "mx-auto flex h-topbar w-full max-w-[430px] md:max-w-[720px] items-center gap-3 px-3", children: [_jsx("button", { type: "button", onClick: () => navigate(back.to), "aria-label": t('actions.back'), className: "\n              flex h-9 w-9 items-center justify-center\n              text-cyan-text-secondary transition active:scale-[0.94]\n              hover:text-cyan-core\n              -ml-1\n            ", children: _jsx(ChevronLeft, { size: 20 }) }), _jsx("h1", { className: "flex-1 truncate text-center font-display text-[15px] font-medium tracking-[-0.02em] text-cyan-text", children: back.title }), _jsx(Link, { to: "/settings", "aria-label": t('nav.settings_aria'), className: "\n              flex h-9 w-9 items-center justify-center\n              text-cyan-text-secondary transition active:scale-[0.94]\n              hover:text-cyan-core\n            ", children: _jsx(SettingsIcon, { size: 18, strokeWidth: 1.6, "aria-hidden": true }) })] }) }));
    }
    const userState = loadUserState();
    const initials = getInitials(userState.profile.displayName);
    const firstName = getFirstName(userState.profile.displayName);
    const greeting = firstName
        ? t('nav.greeting', { name: firstName })
        : t('nav.greeting_anon');
    return (_jsx("header", { className: "\n        sticky top-0 z-40\n        bg-cyan-surface\n        border-b border-cyan-border-faint\n        pt-[env(safe-area-inset-top)]\n      ", children: _jsxs("div", { className: "mx-auto flex h-topbar w-full max-w-[430px] md:max-w-[720px] items-center justify-between gap-3 px-5", children: [_jsx(Link, { to: "/profile", "aria-label": t('nav.profile_aria'), className: "\n            flex h-9 w-9 items-center justify-center\n            bg-cyan-core text-cyan-surface\n            font-display text-[12px] font-medium tracking-[-0.02em]\n            transition active:scale-[0.96]\n          ", children: _jsx("span", { "aria-hidden": true, children: initials }) }), _jsx("p", { className: "flex-1 truncate font-display text-[15px] font-medium tracking-[-0.02em] text-cyan-text", children: greeting }), _jsx(Link, { to: "/settings", "aria-label": t('nav.settings_aria'), className: "\n            flex h-9 w-9 items-center justify-center\n            text-cyan-text-secondary transition active:scale-[0.94]\n            hover:text-cyan-core\n          ", children: _jsx(SettingsIcon, { size: 18, strokeWidth: 1.6, "aria-hidden": true }) })] }) }));
}
//# sourceMappingURL=TopBar.js.map